import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useIncidentBoard, useSeedIncident, useCreateIncidentSite, useExcludeSite, useAutoSector, useBulkSector, useRefreshAddresses, useAdvanceRound,
  type BoardCounts, type IncidentLogEntry, type IncidentPerson, type IncidentSite,
  type SectorAssignment,
} from '../api';
import {
  SITE_ACCOUNTABILITY, accountedMeta, formatTerm, stripHtml, structureMeta, triageMeta,
} from '../incidentVocab';
import RelativeTime from '../components/RelativeTime';
import StatusPopover from '../components/StatusPopover';
import SiteControls from '../components/SiteControls';
import LogRail from '../components/LogRail';
import BoardMap from '../components/BoardMap';
import SectorHeader from '../components/SectorHeader';
import GoKit from '../components/GoKit';
import CommandPanel from '../components/CommandPanel';
import Facilities from '../components/Facilities';
import { printPage } from '../print';
import { SLICES, layerLabel, layersOf, sliceById } from '../incidentSlices';
import { formatPhone } from '../phone';

type Filter = 'all' | 'unaccounted' | 'unswept' | 'injured' | 'needs' | 'medical';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all',         label: 'All' },
  { value: 'unaccounted', label: 'Unaccounted' },
  { value: 'unswept',     label: 'Unswept' },
  { value: 'injured',     label: 'Injured' },
  { value: 'needs',       label: 'Has needs' },
  { value: 'medical',     label: 'Medical/special' },
];

export default function IncidentBoard() {
  const { id = '' } = useParams();
  const { data, isLoading, error } = useIncidentBoard(id);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The ward's own division is the one that matters; 'geo' was only ever
  // the landing spot for the ad-hoc street/block presets.
  const [layer, setLayer] = useState('macro');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [slice, setSlice] = useState<string | null>(null);

  // All filtering is a pure fold over the single board payload — no refetch, so
  // it stays instant on a degraded connection. That is the whole reason
  // GET /board returns everything at once.
  const sites = data?.sites ?? [];
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matchesText = (s: IncidentSite) =>
      !needle ||
      s.label.toLowerCase().includes(needle) ||
      (s.formatted_address ?? '').toLowerCase().includes(needle) ||
      Object.values(s.sectors ?? {}).some((v) => v.toLowerCase().includes(needle)) ||
      s.people.some((p) => p.name.toLowerCase().includes(needle));

    const matchesFilter = (s: IncidentSite) => {
      switch (filter) {
        case 'unaccounted': return s.people.some((p) => p.accounted !== 'accounted');
        case 'unswept':     return !s.swept_this_round;
        case 'injured':     return s.people.some((p) => p.triage === 'red' || p.triage === 'yellow');
        case 'needs':       return s.people.some((p) => p.needs.length > 0);
        case 'medical':     return s.people.some((p) => p.medical_needs || p.special_needs.length > 0);
        default:            return true;
      }
    };
    const sl = sliceById(slice);
    // Facilities have their own panel; leaving them in the sweep list would
    // put the command post in a sector sheet as a house to be knocked on.
    return sites.filter((s) =>
      !s.excluded && s.site_kind === 'residence'
      && matchesText(s) && matchesFilter(s) && (!sl || sl.match(s)));
  }, [sites, filter, q, slice]);

  const skipped = useMemo(() => sites.filter((s) => s.excluded), [sites]);

  const bySector = useMemo(() => {
    const groups = new Map<string, IncidentSite[]>();
    for (const s of visible) {
      const key = s.sectors?.[layer]?.trim() || 'Unassigned';
      const list = groups.get(key);
      if (list) list.push(s); else groups.set(key, [s]);
    }
    return [...groups.entries()].sort(([a], [b]) =>
      a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b));
  }, [visible]);

  const layers = useMemo(() => {
    const found = layersOf(sites);
    // Offer both ward layers even before anything is assigned, so the board
    // shows what it is going to be divided by rather than a single empty tab.
    return found.length ? found : ['macro', 'micro'];
  }, [sites]);

  const assignmentFor = useMemo(() => {
    const m = new Map<string, SectorAssignment>();
    for (const a of data?.assignments ?? []) m.set(`${a.layer}\u0000${a.sector}`, a);
    return (l: string, sec: string) => m.get(`${l}\u0000${sec}`);
  }, [data]);

  const sectorNames = useMemo(
    () => [...new Set(sites.map((s) => s.sectors?.[layer]?.trim()).filter(Boolean) as string[])].sort(),
    [sites, layer],
  );

  if (isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading board…</p>;
  if (error) return <p className="text-red-600 dark:text-red-400">Error loading board.</p>;
  if (!data) return null;

  const { incident, counts } = data;
  const selectedSite = sites.find((s) => s.id === selected) ?? null;

  return (
    <div className="space-y-3">
      {incident.is_drill && (
        <div className="rounded bg-amber-500 px-3 py-1 text-center text-sm font-semibold uppercase tracking-widest text-white">
          Drill — not a real event
        </div>
      )}

      <BoardHeader incident={incident} counts={counts} id={id} setAdding={setAdding} layer={layer} slice={slice} sectorCount={sectorNames.length} />

      <SectorBar
        id={id}
        layers={layers}
        layer={layer}
        setLayer={setLayer}
        slice={slice}
        setSlice={setSlice}
        sectorCount={sectorNames.length}
        sectorNames={sectorNames}
      />

      <div className="flex flex-wrap items-center gap-2 print:hidden">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-3 py-1 text-xs ${
              filter === f.value
                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {f.label}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, address, sector…"
          className="ml-auto w-64 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        />
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Showing {visible.length} of {sites.length} addresses
        {counts.sites_off_map > 0 && (
          <> · <span className="text-amber-700 dark:text-amber-400">
            {counts.sites_off_map} without coordinates (won't appear on a map)
          </span></>
        )}
      </p>

      {sites.length === 0
        ? <NotSeeded id={id} />
        : (
          <div className="grid gap-3 lg:grid-cols-[1fr_22rem]">
            <div className="space-y-4">
              {bySector.map(([sector, group]) => (
                <SectorGroup
                  key={sector}
                  sector={sector}
                  sites={group}
                  allSites={sites}
                  sectors={sectorNames}
                  layer={layer}
                  assignment={assignmentFor(layer, sector)}
                  picked={picked}
                  setPicked={setPicked}
                  incidentId={id}
                  selected={selected}
                  onSelect={(sid) => setSelected((cur) => (cur === sid ? null : sid))}
                />
              ))}
              {bySector.length === 0 && (
                <p className="rounded border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400">
                  No addresses match this filter.
                </p>
              )}
              {picked.size > 0 && (
                <MoveSelectedBar
                  id={id} layer={layer} picked={picked}
                  sectorNames={sectorNames} onDone={() => setPicked(() => new Set())}
                />
              )}
              <AddSiteRow incidentId={id} sectors={sectorNames} layer={layer} open={adding} setOpen={setAdding} />
              {skipped.length > 0 && (
                <SkippedSection sites={skipped} incidentId={id} />
              )}
            </div>
            <div className="space-y-3">
              <Facilities incidentId={id} facilities={data.facilities} sites={sites}
                          commandPostId={incident.command_post_site_id} />
              <CommandPanel incident={incident} />
              <GoKit incidentId={id} items={data.checklist} />
              <BoardMap sites={visible} />
              <SidePanel site={selectedSite} logTail={data.log_tail} />
              <LogRail incidentId={id} tasks={data.open_tasks} entries={data.log_tail} />
            </div>
          </div>
        )}
    </div>
  );
}

function BoardHeader({ incident, counts, id, setAdding, layer, slice, sectorCount }: {
  incident: { name: string; status: string; current_round: number; seeded_at: string | null };
  counts: BoardCounts;
  id: string;
  setAdding: (v: boolean) => void;
  layer: string;
  slice: string | null;
  sectorCount: number;
}) {
  const pct = counts.people_total
    ? Math.round((counts.accounted / counts.people_total) * 100)
    : 0;

  return (
    <header className="sticky top-0 z-10 rounded border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 px-3 py-2 backdrop-blur">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{incident.name}</h1>
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <RoundControl id={id} round={incident.current_round} />
            <span>· {incident.status} ·</span>
            <Link to="/incidents" className="hover:underline">all incidents</Link>
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            onClick={() => setAdding(true)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + Address
          </button>
          <SeedButton id={id} seeded={!!incident.seeded_at} />
          <RefreshAddressesButton id={id} />
          <Link
            to={`/incidents/${id}/packet?layer=${encodeURIComponent(layer)}${slice ? `&slice=${slice}` : ''}`}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            title="Packet PDF of exactly what you're looking at — cover, contacts and comms, a map and roster per sector, and blank log sheets"
          >
            Packet
          </Link>
          <button
            onClick={printPage}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Print
          </button>
        </div>
      </div>

      {/* The number reported up the chain. Derived from `accounted` alone, so
          it can never disagree with the rows below. */}
      <div className="mt-2 flex flex-wrap items-end gap-x-6 gap-y-2">
        <div>
          <div className="text-3xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {counts.accounted}<span className="text-xl text-slate-400 dark:text-slate-500">/{counts.people_total}</span>
          </div>
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">accounted ({pct}%)</div>
        </div>
        <Stat label="unknown" value={counts.unknown} tone={counts.unknown ? 'text-amber-700 dark:text-amber-300' : undefined} />
        <Stat label="missing" value={counts.missing} tone={counts.missing ? 'text-rose-700 dark:text-rose-300 font-semibold' : undefined} />
        <Stat label="not home" value={counts.not_home} />
        <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />
        <Stat label="immediate" value={counts.triage_red} tone={counts.triage_red ? 'text-rose-700 dark:text-rose-300 font-semibold' : undefined} />
        <Stat label="delayed" value={counts.triage_yellow} tone={counts.triage_yellow ? 'text-amber-700 dark:text-amber-300' : undefined} />
        <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />
        <Stat label={`swept · round ${incident.current_round}`} value={`${counts.sites_swept}/${counts.sites_total}`} />
        <Stat label="sectors" value={sectorCount} />
        <Stat label="unsafe" value={counts.placard_red} tone={counts.placard_red ? 'text-rose-700 dark:text-rose-300' : undefined} />
        <Stat label="hazards" value={counts.with_hazards} tone={counts.with_hazards ? 'text-amber-700 dark:text-amber-300' : undefined} />
      </div>
    </header>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div>
      <div className={`text-xl font-semibold tabular-nums ${tone ?? 'text-slate-700 dark:text-slate-200'}`}>{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

function SeedButton({ id, seeded }: { id: string; seeded: boolean }) {
  const seed = useSeedIncident();
  const [result, setResult] = useState<string | null>(null);

  return (
    <span className="flex items-center gap-2">
      <button
        onClick={() =>
          seed.mutate(
            { id, carry_forward_from: 'latest' },
            {
              onSuccess: (r) =>
                setResult(
                  r.sites_created || r.people_created
                    ? `+${r.sites_created} addresses, +${r.people_created} people`
                    + (r.contacts_carried ? `, ${r.contacts_carried} contacts carried` : '')
                    : 'already up to date',
                ),
            },
          )
        }
        disabled={seed.isPending}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
        title="Snapshot current households and people into this incident. Safe to run again — it only adds what's missing."
      >
        {seed.isPending ? 'Seeding…' : seeded ? 'Re-seed' : 'Seed from roster'}
      </button>
      {result && <span className="text-xs text-slate-500 dark:text-slate-400">{result}</span>}
      {seed.error && <span className="text-xs text-rose-600 dark:text-rose-400">{(seed.error as Error).message}</span>}
    </span>
  );
}

function NotSeeded({ id }: { id: string }) {
  return (
    <div className="rounded border border-dashed border-slate-300 dark:border-slate-700 p-6 text-sm text-slate-600 dark:text-slate-400">
      <p className="font-medium text-slate-800 dark:text-slate-200">No addresses yet.</p>
      <p className="mt-1 max-w-2xl">
        Seeding copies every current household and person into this incident as a snapshot. It
        never writes back to the ward roster, and a later LCR sync can't disturb it. Running it
        again is safe — it only adds what's missing.
      </p>
      <div className="mt-3">
        <SeedButton id={id} seeded={false} />
      </div>
    </div>
  );
}

function SectorGroup({ sector, sites, allSites, sectors, layer, assignment, picked, setPicked, incidentId, selected, onSelect }: {
  sector: string;
  sites: IncidentSite[];
  allSites: IncidentSite[];
  sectors: string[];
  layer: string;
  assignment: SectorAssignment | undefined;
  picked: Set<string>;
  setPicked: (f: (p: Set<string>) => Set<string>) => void;
  incidentId: string;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const people = sites.flatMap((s) => s.people);
  const accounted = people.filter((p) => p.accounted === 'accounted').length;

  return (
    <section className="rounded border border-slate-200 dark:border-slate-700">
      <div className="flex w-full items-center justify-between px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50">
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-left text-sm font-medium text-slate-800 dark:text-slate-200"
        >
          <span className="mr-1 inline-block w-3 text-slate-400">{open ? '▾' : '▸'}</span>
          {sector}
        </button>
        <span className="flex items-center gap-3">
          <SectorHeader incidentId={incidentId} layer={layer} sector={sector} assignment={assignment} />
          <Link
            to={`/incidents/${incidentId}/packet?layer=${encodeURIComponent(layer)}&sector=${encodeURIComponent(sector)}`}
            onClick={(e) => e.stopPropagation()}
            className="rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 print:hidden"
            title={`Print the packet for ${sector} only — same format, just this sector`}
          >
            Print sector
          </Link>
          <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
            {sites.length} addresses · {accounted}/{people.length} accounted
          </span>
        </span>
      </div>
      {open && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 border-t border-slate-200 dark:border-slate-700">
          {sites.map((s) => (
            <SiteRow
              key={s.id}
              site={s}
              picked={picked.has(s.id)}
              onPick={() => setPicked((p) => {
                const n = new Set(p);
                if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
                return n;
              })}
              allSites={allSites}
              sectors={sectors}
              layer={layer}
              incidentId={incidentId}
              selected={s.id === selected}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SiteRow({ site, allSites, sectors, layer, incidentId, picked, onPick, selected, onSelect }: {
  site: IncidentSite;
  allSites: IncidentSite[];
  sectors: string[];
  layer: string;
  incidentId: string;
  picked: boolean;
  onPick: () => void;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const roll = SITE_ACCOUNTABILITY[site.accountability];
  const placard = structureMeta(site.structure);
  const [openPerson, setOpenPerson] = useState<string | null>(null);

  return (
    <li
      onClick={() => onSelect(site.id)}
      className={`cursor-pointer px-3 py-1.5 ${
        selected ? 'bg-sky-50 dark:bg-sky-950/40' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }`}
    >
      <div className="flex items-baseline gap-2">
        <input
          type="checkbox"
          checked={picked}
          onChange={onPick}
          onClick={(e) => e.stopPropagation()}
          title="Select to move to another sector"
          className="print:hidden"
        />
        <span className="font-medium text-slate-900 dark:text-slate-100">{site.label}</span>
        {site.household_id === null && (
          <span
            className="rounded bg-violet-100 dark:bg-violet-900/50 px-1 text-xs text-violet-700 dark:text-violet-300"
            title="Added during this incident — not in the ward roster"
          >
            added
          </span>
        )}
        <span className="truncate text-xs text-slate-500 dark:text-slate-400">{stripHtml(site.formatted_address)}</span>
        <span className={`ml-auto shrink-0 text-xs ${roll.tone}`}>{roll.label}</span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {site.people.map((p) => (
          <span key={p.id} className="relative">
            <PersonChip
              person={p}
              onClick={(e) => {
                e.stopPropagation();
                setOpenPerson((cur) => (cur === p.id ? null : p.id));
              }}
            />
            {openPerson === p.id && (
              <StatusPopover
                person={p}
                incidentId={incidentId}
                sites={allSites}
                onClose={() => setOpenPerson(null)}
              />
            )}
          </span>
        ))}
        {site.people.length === 0 && (
          <span className="text-xs italic text-slate-400 dark:text-slate-500">no occupants recorded</span>
        )}

        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {site.structure !== 'unknown' && (
            <span className={`rounded px-1 text-xs ${placard.tone}`} title={`Structure: ${placard.label}`}>
              {placard.label}
            </span>
          )}
          {site.hazards.map((h) => (
            <span key={h} className="rounded bg-amber-100 dark:bg-amber-900/50 px-1 text-xs text-amber-800 dark:text-amber-300">
              {formatTerm(h)}
            </span>
          ))}
          <RelativeTime at={site.last_checked_at} never="unswept" />
        </span>
      </div>

      {selected && (
        <div onClick={(e) => e.stopPropagation()} className="print:hidden">
          <SiteControls site={site} incidentId={incidentId} layer={layer} sectors={sectors} />
        </div>
      )}
    </li>
  );
}

/** Status is carried by a letter first and colour second, so the chip survives
 *  a grayscale print and does not rely on colour discrimination. */
function PersonChip({ person: p, onClick }: {
  person: IncidentPerson;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const acc = accountedMeta(p.accounted);
  const tri = triageMeta(p.triage);
  const flagged = p.medical_needs || p.special_needs.length > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded border border-slate-200 dark:border-slate-700 py-0.5 pl-0.5 pr-1.5 text-xs hover:border-slate-400 dark:hover:border-slate-500"
      title={[
        p.name,
        `Accounted: ${acc.label}`,
        tri ? `Triage: ${tri.label}` : null,
        p.needs.length ? `Needs: ${p.needs.map(formatTerm).join(', ')}` : null,
        p.special_needs.length ? `Special: ${p.special_needs.map(formatTerm).join(', ')}` : null,
        p.medical_needs,
        p.disposition,
      ].filter(Boolean).join(' · ')}
    >
      <span className={`grid h-4 w-4 place-items-center rounded-sm font-semibold ${acc.tone}`}>
        {acc.short}
      </span>
      <span className="text-slate-700 dark:text-slate-300">{p.name}</span>
      {tri && <span className={`rounded-sm px-1 font-semibold ${tri.tone}`}>{tri.short}</span>}
      {p.needs.length > 0 && (
        <span className="rounded-sm bg-sky-100 px-1 text-sky-800 dark:bg-sky-900/60 dark:text-sky-300">
          {p.needs.length} need{p.needs.length > 1 ? 's' : ''}
        </span>
      )}
      {flagged && <span className="text-violet-600 dark:text-violet-400" title="Medical or special needs">✚</span>}
    </button>
  );
}

function SidePanel({ site, logTail }: {
  site: IncidentSite | null;
  logTail: IncidentLogEntry[];
}) {
  if (!site) {
    return (
      <aside className="hidden lg:block rounded border border-dashed border-slate-300 dark:border-slate-700 p-3 text-xs text-slate-500 dark:text-slate-400">
        Select an address to see occupants, medical needs, emergency contacts, and its log.
      </aside>
    );
  }
  const siteLog = logTail.filter((e) => e.site_id === site.id);

  return (
    <aside className="space-y-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm lg:sticky lg:top-32 lg:self-start">
      <div>
        <h3 className="font-medium text-slate-900 dark:text-slate-100">{site.label}</h3>
        {site.address_lines.map((l, i) => (
          <p key={i} className="text-xs text-slate-500 dark:text-slate-400">{stripHtml(l)}</p>
        ))}
        {Object.entries(site.sectors ?? {}).map(([k, v]) => (
          <p key={k} className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">{k}: {v}</p>
        ))}
      </div>

      <Block title="Occupants">
        {site.people.length === 0 && <Muted>None recorded.</Muted>}
        <ul className="space-y-1.5">
          {site.people.map((p) => (
            <li key={p.id} className="text-xs">
              <div className="flex items-baseline gap-1.5">
                <span className="font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
                {p.age != null && <span className="text-slate-400">{p.age}</span>}
                {p.phone && (
                  <span className="text-slate-500 dark:text-slate-400">{formatPhone(p.phone)}</span>
                )}
              </div>
              <div className="text-slate-500 dark:text-slate-400">
                {accountedMeta(p.accounted).label}
                {p.triage && ` · triage ${triageMeta(p.triage)!.label}`}
                {p.disposition && ` · ${p.disposition}`}
              </div>
              {(p.special_needs.length > 0 || p.medical_needs) && (
                <div className="text-violet-700 dark:text-violet-300">
                  {p.special_needs.map(formatTerm).join(', ')}
                  {p.special_needs.length > 0 && p.medical_needs ? ' — ' : ''}
                  {p.medical_needs}
                </div>
              )}
              {p.contacts.map((c) => (
                <div key={c.id} className="text-slate-500 dark:text-slate-400">
                  ☎ {c.name}{c.relationship ? ` (${c.relationship})` : ''} {formatPhone(c.phone)}
                  {c.is_out_of_area && <span className="ml-1 text-emerald-700 dark:text-emerald-400">out-of-area</span>}
                </div>
              ))}
            </li>
          ))}
        </ul>
      </Block>

      {site.contacts.length > 0 && (
        <Block title="Household contacts">
          <ul className="space-y-1 text-xs">
            {site.contacts.map((c) => (
              <li key={c.id} className="text-slate-600 dark:text-slate-300">
                ☎ {c.name}{c.relationship ? ` (${c.relationship})` : ''} {formatPhone(c.phone)}
                {c.is_out_of_area && <span className="ml-1 text-emerald-700 dark:text-emerald-400">out-of-area</span>}
              </li>
            ))}
          </ul>
        </Block>
      )}

      {site.notes && <Block title="Notes"><p className="text-xs text-slate-600 dark:text-slate-300">{site.notes}</p></Block>}

      {siteLog.length > 0 && (
        <Block title="Log">
          <ul className="space-y-1 text-xs">
            {siteLog.map((e) => (
              <li key={e.id} className="text-slate-600 dark:text-slate-300">
                <RelativeTime at={e.occurred_at} /> {e.body}
              </li>
            ))}
          </ul>
        </Block>
      )}

      <p className="border-t border-slate-100 dark:border-slate-800 pt-2 text-xs text-slate-400 dark:text-slate-500">
        Tap any person chip to set status. Select an address to open its controls.
      </p>
    </aside>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-2">
      <h4 className="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h4>
      {children}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p className="text-xs italic text-slate-400 dark:text-slate-500">{children}</p>;
}

/** An address that is not in the ward roster at all — the neighbor case. */
function AddSiteRow({ incidentId, sectors, layer, open, setOpen }: {
  incidentId: string; sectors: string[]; layer: string; open: boolean; setOpen: (v: boolean) => void;
}) {
  const create = useCreateIncidentSite();
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');
  const [sector, setSector] = useState('');

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded border border-dashed border-slate-300 dark:border-slate-700 py-2 text-sm text-slate-500 dark:text-slate-400 hover:border-slate-400 hover:text-slate-700 dark:hover:text-slate-200 print:hidden"
      >
        + Add an address not in the roster
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!label.trim()) return;
        create.mutate(
          {
            incidentId,
            label: label.trim(),
            formatted_address: address.trim() || null,
            sectors: sector.trim() ? { [layer]: sector.trim() } : {},
          },
          {
            onSuccess: () => {
              setLabel(''); setAddress(''); setOpen(false);
            },
          },
        );
      }}
      className="space-y-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 print:hidden"
    >
      <div className="flex flex-wrap gap-1">
        <input
          autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder='Name or description — "yellow duplex on the corner"'
          className="flex-1 min-w-48 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        />
        <input
          value={address} onChange={(e) => setAddress(e.target.value)}
          placeholder="Address, if known"
          className="w-48 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        />
        <input
          list="incident-sectors" value={sector} onChange={(e) => setSector(e.target.value)}
          placeholder="Sector"
          className="w-32 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        />
        <datalist id="incident-sectors">
          {sectors.map((s) => <option key={s} value={s} />)}
        </datalist>
        <button type="submit" disabled={create.isPending || !label.trim()}
          className="rounded bg-slate-800 dark:bg-slate-200 px-3 py-1 text-sm text-white dark:text-slate-900 disabled:opacity-40">
          Add
        </button>
        <button type="button" onClick={() => setOpen(false)} className="px-2 text-sm text-slate-500">
          Cancel
        </button>
      </div>
      {create.error && (
        <p className="rounded bg-rose-50 dark:bg-rose-950 px-2 py-1 text-xs text-rose-700 dark:text-rose-300">
          {(create.error as Error).message}
        </p>
      )}
    </form>
  );
}

/**
 * Addresses skipped out of this response area.
 *
 * They stay on the payload rather than being deleted, for two reasons: the
 * seed is idempotent by household, so a deleted row simply comes back on the
 * next seed; and anything already recorded against an address survives if it
 * is put back. They are excluded from every count.
 */
function SkippedSection({ sites, incidentId }: { sites: IncidentSite[]; incidentId: string }) {
  const [open, setOpen] = useState(false);
  const exclude = useExcludeSite();

  return (
    <section className="rounded border border-slate-200 dark:border-slate-800 print:hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <span>
          <span className="mr-1 inline-block w-3">{open ? '▾' : '▸'}</span>
          Skipped — not in this response area
        </span>
        <span className="tabular-nums">{sites.length}</span>
      </button>
      {open && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 border-t border-slate-200 dark:border-slate-800">
          {sites.map((s) => (
            <li key={s.id} className="flex items-baseline gap-2 px-3 py-1 text-sm">
              <span className="text-slate-500 dark:text-slate-400">{s.label}</span>
              <span className="truncate text-xs text-slate-400 dark:text-slate-500">
                {stripHtml(s.formatted_address)}
              </span>
              <button
                onClick={() => exclude.mutate({ incidentId, id: s.id, excluded: false })}
                disabled={exclude.isPending}
                className="ml-auto shrink-0 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Put back
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Sector layer switcher, quick-sector presets, and slice chips.
 *
 * Layers and slices sit side by side here because they answer adjacent
 * questions, but they are not the same kind of thing: a layer is an
 * assignment someone made and can edit, a slice is computed from the data
 * every time it is shown. That is why slices need no upkeep and can never go
 * stale, and why only layers get presets.
 */
function SectorBar({ id, layers, layer, setLayer, slice, setSlice, sectorCount, sectorNames }: {
  id: string;
  layers: string[];
  layer: string;
  setLayer: (l: string) => void;
  slice: string | null;
  setSlice: (s: string | null) => void;
  sectorCount: number;
  sectorNames: string[];
}) {
  const auto = useAutoSector();
  const [note, setNote] = useState<string | null>(null);
  const [addingLayer, setAddingLayer] = useState(false);
  const [managing, setManaging] = useState(false);

  const run = (strategy: 'ward') => {
    // 'ward' fills both of its own layers, so the confirmation and the
    // overwrite question are about those, not whichever tab is open.
    const target = strategy === 'ward' ? 'Ward sector + Walk' : layerLabel(layer);
    const hasExisting = strategy === 'ward'
      ? layers.includes('macro') || layers.includes('micro')
      : sectorCount > 0;
    // Re-running a preset must never quietly undo hand adjustments, so
    // overwriting is opt-in and spelled out.
    const overwrite = hasExisting && window.confirm(
      `"${target}" already has sectors assigned.\n\n` +
      'OK — reassign everything from scratch, discarding manual changes.\n' +
      'Cancel — only fill addresses that have no sector yet.',
    );
    auto.mutate({ incidentId: id, layer, strategy, overwrite }, {
      onSuccess: (r) => setNote(
        `${r.assigned} assigned, ${r.kept_existing} left alone` +
        (r.unplaceable ? `, ${r.unplaceable} couldn't be placed` : ''),
      ),
    });
  };

  return (
    <div className="space-y-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 print:hidden">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Group by</span>
        {layers.map((l) => (
          <button
            key={l}
            onClick={() => setLayer(l)}
            className={`rounded px-2 py-0.5 text-xs ${
              layer === l
                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {layerLabel(l)}
          </button>
        ))}
        {addingLayer ? (
          <input
            autoFocus
            placeholder="new layer name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = (e.target as HTMLInputElement).value.trim();
                if (v) setLayer(v);
                setAddingLayer(false);
              }
              if (e.key === 'Escape') setAddingLayer(false);
            }}
            onBlur={() => setAddingLayer(false)}
            className="w-32 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
          />
        ) : (
          <button
            onClick={() => setAddingLayer(true)}
            className="rounded border border-dashed border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
            title="Layers are just names — add one for teams, shifts, or however else you need to divide the same addresses"
          >
            + layer
          </button>
        )}

        <span className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => run('ward')}
            disabled={auto.isPending}
            className="rounded border border-slate-800 bg-slate-800 px-2 py-0.5 text-xs text-white hover:bg-slate-700 disabled:opacity-50 dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white"
            title="Fill both layers from the ward's defined sectors — the ward sector each household belongs to, and the walk within it. Honours any override set on a household."
          >
            {auto.isPending ? 'Assigning…' : 'Assign ward sectors'}
          </button>
          <button
            onClick={() => setManaging((v) => !v)}
            className={`rounded border px-2 py-0.5 text-xs ${
              managing
                ? 'border-slate-800 bg-slate-800 text-white dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900'
                : 'border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title="Combine sectors into one, or rename"
          >
            Combine…
          </button>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Slice</span>
        <button
          onClick={() => setSlice(null)}
          className={`rounded-full px-2 py-0.5 text-xs ${
            slice === null
              ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
              : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          Everyone
        </button>
        {SLICES.map((sl) => (
          <button
            key={sl.id}
            onClick={() => setSlice(slice === sl.id ? null : sl.id)}
            title={sl.description}
            className={`rounded-full px-2 py-0.5 text-xs ${
              slice === sl.id
                ? 'bg-sky-600 text-white dark:bg-sky-500'
                : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {sl.label}
          </button>
        ))}
      </div>

      {managing && (
        <SectorManager id={id} layer={layer} sectorNames={sectorNames} onDone={() => setManaging(false)} />
      )}

      {(note || auto.error) && (
        <p className={`text-xs ${auto.error ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'}`}>
          {auto.error ? (auto.error as Error).message : note}
        </p>
      )}
    </div>
  );
}

/**
 * Combine sectors, or split one apart.
 *
 * These are the same operation underneath — a bulk reassignment within a
 * layer — differing only in how the targets are chosen. Combining picks whole
 * sectors by name; splitting picks individual addresses from the board and
 * moves them (the "Move selected" control on each sector group). Presets
 * produce a reasonable first cut and this is how you fix it: 106 street
 * sectors from 386 addresses is right for the dense corridors and far too
 * granular for the cul-de-sacs.
 */
function SectorManager({ id, layer, sectorNames, onDone }: {
  id: string; layer: string; sectorNames: string[]; onDone: () => void;
}) {
  const bulk = useBulkSector();
  const [picked, setPicked] = useState<string[]>([]);
  const [to, setTo] = useState('');

  const toggle = (s: string) =>
    setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2">
      <p className="mb-1 text-xs text-slate-600 dark:text-slate-300">
        Pick sectors to combine, then name the result. To split instead, select addresses
        on the board and use “Move selected”.
      </p>
      <div className="mb-2 flex max-h-40 flex-wrap gap-1 overflow-y-auto">
        {sectorNames.length === 0 && (
          <span className="text-xs italic text-slate-400">No sectors in this layer yet.</span>
        )}
        {sectorNames.map((s) => (
          <button
            key={s}
            onClick={() => toggle(s)}
            className={`rounded px-1.5 py-0.5 text-xs ${
              picked.includes(s)
                ? 'bg-sky-600 text-white dark:bg-sky-500'
                : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (picked.length === 0) return;
          bulk.mutate({ incidentId: id, layer, from: picked, to: to.trim() }, {
            onSuccess: () => { setPicked([]); setTo(''); onDone(); },
          });
        }}
        className="flex flex-wrap items-center gap-1"
      >
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {picked.length} selected →
        </span>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="New sector name (blank clears)"
          className="w-56 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        />
        <button
          type="submit"
          disabled={bulk.isPending || picked.length === 0}
          className="rounded bg-slate-800 dark:bg-slate-200 px-2 py-0.5 text-xs text-white dark:text-slate-900 disabled:opacity-40"
        >
          {to.trim() ? 'Combine' : 'Clear sector'}
        </button>
        {bulk.error && (
          <span className="text-xs text-rose-600 dark:text-rose-400">{(bulk.error as Error).message}</span>
        )}
      </form>
    </div>
  );
}

/** Split: move a hand-picked set of addresses into a sector of their own. */
function MoveSelectedBar({ id, layer, picked, sectorNames, onDone }: {
  id: string; layer: string; picked: Set<string>; sectorNames: string[]; onDone: () => void;
}) {
  const bulk = useBulkSector();
  const [to, setTo] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        bulk.mutate(
          { incidentId: id, layer, site_ids: [...picked], to: to.trim() },
          { onSuccess: () => { setTo(''); onDone(); } },
        );
      }}
      className="sticky bottom-2 z-10 flex flex-wrap items-center gap-2 rounded border border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-950/70 px-3 py-2 shadow print:hidden"
    >
      <span className="text-sm text-sky-900 dark:text-sky-200">
        {picked.size} address{picked.size === 1 ? '' : 'es'} selected
      </span>
      <input
        list="incident-sectors"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        placeholder="Move to sector…"
        className="w-56 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
      />
      <datalist id="incident-sectors">
        {sectorNames.map((s) => <option key={s} value={s} />)}
      </datalist>
      <button type="submit" disabled={bulk.isPending}
        className="rounded bg-slate-800 dark:bg-slate-200 px-3 py-1 text-sm text-white dark:text-slate-900">
        {to.trim() ? 'Move' : 'Clear sector'}
      </button>
      <button type="button" onClick={onDone} className="text-sm text-slate-500">Cancel</button>
      {bulk.error && <span className="text-xs text-rose-600 dark:text-rose-400">{(bulk.error as Error).message}</span>}
    </form>
  );
}

/**
 * Pull current roster names and addresses onto this incident.
 *
 * Sites are a snapshot, so an incident seeded before an address cleanup keeps
 * the old spellings — and Seed will not fix it, because Seed is deliberately
 * additive-only so it can never reset progress mid-response. This is the
 * explicit "yes, update what came from the roster" action, and it leaves
 * every recorded status, sector, and note alone.
 */
function RefreshAddressesButton({ id }: { id: string }) {
  const refresh = useRefreshAddresses();
  const [note, setNote] = useState<string | null>(null);

  return (
    <span className="flex items-center gap-1.5">
      <button
        onClick={() =>
          refresh.mutate({ incidentId: id, dry_run: true }, {
            onSuccess: (r) => {
              if (r.changed === 0) { setNote('addresses already current'); return; }
              const preview = r.samples.slice(0, 5)
                .map((s) => `  ${s.before}\n→ ${s.after}`).join('\n\n');
              const ok = window.confirm(
                `Update names and addresses on ${r.changed} of ${r.sites} addresses `
                + `from the current roster?\n\n`
                + `Accountability, sectors, structure, hazards, and notes are NOT touched.\n\n`
                + (preview ? `For example:\n\n${preview}` : ''),
              );
              if (ok) {
                refresh.mutate({ incidentId: id }, {
                  onSuccess: (d) => setNote(`${d.changed} addresses updated`),
                });
              }
            },
          })
        }
        disabled={refresh.isPending}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
        title="Pull current names and addresses from the roster onto this incident, without disturbing anything recorded during the response"
      >
        {refresh.isPending ? 'Checking…' : 'Refresh addresses'}
      </button>
      {note && <span className="text-xs text-slate-500 dark:text-slate-400">{note}</span>}
      {refresh.error && (
        <span className="text-xs text-rose-600 dark:text-rose-400">{(refresh.error as Error).message}</span>
      )}
    </span>
  );
}

/**
 * Which sweep pass we are on, and how to start the next one.
 *
 * Rounds exist because accountability is never one pass: round 1 knocks every
 * door, round 2 goes back to the no-answers. "Swept" always means swept THIS
 * round, so advancing makes every address read as needing another visit
 * without erasing anything recorded on the previous pass.
 */
function RoundControl({ id, round }: { id: string; round: number }) {
  const advance = useAdvanceRound();
  const [note, setNote] = useState<string | null>(null);

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        title="A sweep pass. Round 1 is the first knock on every door; round 2 revisits whatever round 1 could not reach."
        className="cursor-help border-b border-dotted border-slate-400"
      >
        Round {round}
      </span>
      <button
        onClick={() => {
          if (!window.confirm(
            `Start round ${round + 1}?\n\n`
            + `Every address will read as needing a visit again, so you can work the ones `
            + `round ${round} could not reach.\n\n`
            + `Nothing recorded is erased — statuses, placards, hazards, and notes all stay.`,
          )) return;
          advance.mutate({ incidentId: id }, {
            onSuccess: (r) => setNote(`round ${r.current_round} · ${r.addresses_pending} to visit`),
          });
        }}
        disabled={advance.isPending}
        className="rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 print:hidden"
      >
        Start round {round + 1}
      </button>
      {note && <span className="text-emerald-700 dark:text-emerald-300">{note}</span>}
      {advance.error && <span className="text-rose-600 dark:text-rose-400">{(advance.error as Error).message}</span>}
    </span>
  );
}
